import { useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import FilterBar from "@/components/admin/FilterBar";
import PaymentBadge from "@/components/admin/PaymentBadge";
import { exportCsv } from "@/lib/csv";
import { toast } from "sonner";
import {
    BOOKING_FILTER_FIELDS_OPTIONS,
    EMPTY_BASIC_FILTERS,
    matchesText,
    withinDateRange,
} from "@/components/admin/adminFilters";

export default function PaymentsTab({ payments }) {
    const [filters, setFilters] = useState({ ...EMPTY_BASIC_FILTERS, payment_status: "" });

    const filtered = useMemo(() => {
        const f = filters;
        return payments.filter((p) => {
            if (
                !matchesText(
                    [
                        p.reference_id,
                        p.service_key,
                        p.order_no,
                        p.txn_number,
                        p.mpg_txn_number,
                    ],
                    f.q
                )
            )
                return false;
            if (f.payment_status && p.payment_status !== f.payment_status) return false;
            const created = (p.created_at || "").slice(0, 10);
            if ((f.date_from || f.date_to) && !withinDateRange(created, f.date_from, f.date_to))
                return false;
            return true;
        });
    }, [payments, filters]);

    function exportRows() {
        const headers = [
            { key: "reference_id", label: "Reference" },
            { key: "service_key", label: "Service" },
            { key: "amount", label: "Amount" },
            { key: "currency", label: "Currency" },
            { key: "payment_status", label: "Payment status" },
            { key: "status", label: "Session status" },
            { key: "provider", label: "Provider" },
            { key: "order_no", label: "Order #" },
            { key: "txn_number", label: "Moneris txn #" },
            { key: "approval_code", label: "Approval code" },
            { key: "created_at", label: "Created at" },
        ];
        exportCsv(`payments_${new Date().toISOString().slice(0, 10)}.csv`, headers, filtered);
        toast.success(`Exported ${filtered.length} payment(s)`);
    }

    return (
        <>
            <FilterBar
                fields={[
                    { key: "q", label: "Search", type: "text" },
                    {
                        key: "payment_status",
                        label: "Status",
                        type: "select",
                        options: BOOKING_FILTER_FIELDS_OPTIONS.PAYMENT_STATUS,
                    },
                    { key: "date_from", label: "Created from", type: "date" },
                    { key: "date_to", label: "Created to", type: "date" },
                ]}
                value={filters}
                onChange={setFilters}
                onExport={exportRows}
                resultCount={filtered.length}
                totalCount={payments.length}
            />
            <div className="card-soft mt-4 overflow-x-auto p-4">
                <Table data-testid="payments-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Reference</TableHead>
                            <TableHead>Service</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Provider</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.map((p) => (
                            <TableRow key={p.id}>
                                <TableCell className="font-mono text-xs">{p.reference_id}</TableCell>
                                <TableCell>{p.service_label || p.service_key}</TableCell>
                                <TableCell>
                                    ${p.amount} {p.currency?.toUpperCase()}
                                </TableCell>
                                <TableCell className="text-xs uppercase tracking-wider text-[#0E2A47]/70">
                                    {p.provider === "moneris_checkout"
                                        ? "moneris checkout"
                                        : p.provider === "moneris_gateway"
                                          ? "moneris (legacy)"
                                          : p.provider || (p.session_id ? "stripe" : "—")}
                                </TableCell>
                                <TableCell>
                                    <PaymentBadge status={p.payment_status} />
                                </TableCell>
                                <TableCell className="text-xs text-[#475569]">
                                    {p.created_at?.slice(0, 16)}
                                </TableCell>
                            </TableRow>
                        ))}
                        {filtered.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} className="py-8 text-center text-sm text-[#475569]">
                                    No payments match these filters.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}
